import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import translate from '../_utils/tools/translate';
import PropTypes from 'prop-types';

const parseTags = (tags) => {
  const map = Object.fromEntries((tags || []).map((t) => t.split(':', 2)));
  return { color: map.dominant_colour || null, sentiment: map.sentiment || null };
};

const Variants = ({ variants, selectedVariant, onVariantSelected }) => {
  const { colorOptions, sentimentOptions, tagsByKey, byPair, byColor, bySentiment } = useMemo(() => {
    const empty = { colorOptions: [], sentimentOptions: [], tagsByKey: {}, byPair: {}, byColor: {}, bySentiment: {} };
    if (!variants || Object.keys(variants).length < 2) return empty;

    const tagsByKey = {};
    const byPair = {};
    const byColor = {};
    const bySentiment = {};

    Object.entries(variants).forEach(([key, data]) => {
      const tags = parseTags(data.tags);
      if (data.dominant_colours?.[0]) tags.color = data.dominant_colours[0];
      tagsByKey[key] = tags;
      if (tags.color) byColor[tags.color] ||= key;
      if (tags.sentiment) bySentiment[tags.sentiment] ||= key;
      if (tags.color && tags.sentiment) byPair[`${tags.color}|${tags.sentiment}`] ||= key;
    });

    return {
      colorOptions: Object.keys(byColor),
      sentimentOptions: Object.keys(bySentiment).map((s) => ({
        value: s,
        label: s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      })),
      tagsByKey,
      byPair,
      byColor,
      bySentiment,
    };
  }, [variants]);

  const selected = tagsByKey[selectedVariant] || { color: null, sentiment: null };
  const [selectedColor, setSelectedColor] = useState(selected.color);
  const [selectedSentiment, setSelectedSentiment] = useState(selected.sentiment);

  useEffect(() => {
    setSelectedColor(selected.color);
    setSelectedSentiment(selected.sentiment);
  }, [selected.color, selected.sentiment]);

  if (colorOptions.length === 0 && sentimentOptions.length === 0) return null;

  const findVariant = (color, sentiment) =>
    byPair[`${color}|${sentiment}`] || byColor[color] || bySentiment[sentiment] || selectedVariant;

  const handleColorClick = (color) => {
    setSelectedColor(color);
    onVariantSelected(findVariant(color, selectedSentiment));
  };

  const handleSentimentClick = (sentiment) => {
    setSelectedSentiment(sentiment);
    onVariantSelected(findVariant(selectedColor, sentiment));
  };

  return (
    <div className="d-flex flex-column">
      {colorOptions.length > 1 && (
        <div className="mt-2">
          <strong className="title">Dominant {`${translate('Colour')}`}:</strong>
          <div className="d-flex flex-row flex-wrap">
            {colorOptions.map((color) => {
              const isSelected = selectedColor === color;

              return (
                <div
                  key={color}
                  className={classNames('dominant-colour d-flex mr-2', { selected: isSelected })}
                  data-test-id={isSelected ? 'selected' : 'unselected'}
                >
                  <button
                    className="btn dominant-colour-button align-self-center p-0"
                    onClick={() => handleColorClick(color)}
                    style={{ background: color }}
                    aria-label={`Select color ${color}`}
                    type="button"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sentimentOptions.length > 1 && (
        <div className="mt-2">
          <strong className="title">Sentiment:</strong>
          <div className="d-flex flex-row flex-wrap">
            {sentimentOptions.map(({ value, label }) => {
              const isSelected = selectedSentiment === value;

              return (
                <button
                  key={value}
                  className={classNames('btn mr-2 mb-2 px-3 py-2 rounded', {
                    'btn-primary': isSelected,
                    'btn-outline-secondary': !isSelected,
                  })}
                  onClick={() => handleSentimentClick(value)}
                  style={{
                    background: isSelected ? '#f0f0f0' : 'transparent',
                    color: '#333',
                    border: `2px solid ${isSelected ? '#333' : '#ccc'}`,
                  }}
                  aria-label={`Select sentiment ${label}`}
                  type="button"
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

Variants.propTypes = {
  variants: PropTypes.objectOf(
    PropTypes.shape({
      tags: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  selectedVariant: PropTypes.string,
  onVariantSelected: PropTypes.func.isRequired,
};

export default Variants;
